import { useState } from 'react';
import { VideoPreviewData } from './VideoPreviewModal';
import { COMMUNITY_VIDEOS, CommunityVideoEntry } from '../videoData';

const STYLE_COLORS: Record<string, string> = {
  Cinematic: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  Realistic: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  Animated: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  Abstract: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

interface CommunityVideosProps {
  darkMode: boolean;
  onRemix: (prompt: string) => void;
  onPreview: (video: VideoPreviewData) => void;
}

export default function CommunityVideos({ darkMode, onRemix, onPreview }: CommunityVideosProps) {
  const [likedSet, setLikedSet] = useState<Set<string>>(new Set());
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [filterStyle, setFilterStyle] = useState<string>('All');

  const styles = ['All', 'Cinematic', 'Realistic', 'Animated', 'Abstract'];

  const filtered: CommunityVideoEntry[] = filterStyle === 'All'
    ? COMMUNITY_VIDEOS
    : COMMUNITY_VIDEOS.filter(v => v.style === filterStyle);

  const toggleLike = (id: string) => {
    setLikedSet(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className={`rounded-2xl border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-5 h-5 flex items-center justify-center text-red-500">
                <i className="ri-community-line text-base"></i>
              </span>
              <h3 className={`text-base font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Community Videos
              </h3>
              <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${darkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-600'}`}>
                Trending
              </span>
            </div>
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              AI-generated videos by the community — click any thumbnail to preview
            </p>
          </div>
          <a href="#" className="text-xs text-red-500 font-semibold hover:underline whitespace-nowrap">See all →</a>
        </div>

        {/* Style filter */}
        <div className="flex gap-2 flex-wrap">
          {styles.map(s => (
            <button
              key={s}
              onClick={() => setFilterStyle(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                filterStyle === s
                  ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
                  : darkMode
                  ? 'bg-gray-900 text-gray-400 hover:bg-gray-700'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(video => {
          const isLiked = likedSet.has(video.id);
          const isHovered = hoveredId === video.id;
          return (
            <div
              key={video.id}
              onMouseEnter={() => setHoveredId(video.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={`rounded-xl overflow-hidden border transition-all duration-200 ${
                darkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
              } ${isHovered ? (darkMode ? 'border-red-600' : 'border-red-300') : ''}`}
            >
              {/* Thumbnail */}
              <div
                className="relative cursor-pointer"
                style={{ height: '160px' }}
                onClick={() => onPreview({
                  id: video.id,
                  thumbnail: video.thumbnail,
                  prompt: video.prompt,
                  style: video.style,
                  duration: video.duration,
                  author: video.author,
                  avatar: video.avatar,
                  views: video.views,
                  videoType: video.videoType,
                })}
              >
                <img
                  src={video.thumbnail}
                  alt={video.prompt}
                  className="w-full h-full object-cover object-top transition-transform duration-500"
                  style={{ transform: isHovered ? 'scale(1.04)' : 'scale(1)' }}
                />
                {/* Overlay on hover */}
                <div className={`absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-2 transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                    <i className="ri-play-fill text-red-500 text-2xl ml-1"></i>
                  </div>
                  <span className="text-white text-xs font-semibold">Preview</span>
                </div>
                {/* Style badge */}
                <div className="absolute top-2 left-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STYLE_COLORS[video.style] ?? ''}`}>
                    {video.style}
                  </span>
                </div>
                {/* Duration badge */}
                <div className="absolute top-2 right-2">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/60 text-white">
                    {video.duration}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="p-3">
                {/* Author */}
                <div className="flex items-center gap-2 mb-2">
                  <img
                    src={video.avatar}
                    alt={video.author}
                    className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                  />
                  <span className={`text-xs font-semibold truncate ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{video.author}</span>
                  <span className={`ml-auto text-[10px] flex items-center gap-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <i className="ri-eye-line"></i>{video.views}
                  </span>
                </div>

                {/* Prompt preview */}
                <p className={`text-xs leading-relaxed line-clamp-2 mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {video.prompt}
                </p>

                {/* Action row */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onRemix(video.prompt)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-semibold hover:shadow-md transition-all whitespace-nowrap"
                  >
                    <i className="ri-loop-right-line text-sm"></i>
                    Remix Prompt
                  </button>
                  <button
                    onClick={() => toggleLike(video.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                      isLiked
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400'
                        : darkMode
                        ? 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                    }`}
                  >
                    <i className={`${isLiked ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
                    {video.likes + (isLiked ? 1 : 0)}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
